import React from "react";

/* Opportunity map review — "Here's what we'll look for."

   Renders a search_plan in either read-only or editable mode. Fully
   controlled: the parent owns the plan state and gets every edit through
   `onChange(nextPlan)`. There is no internal draft — the screen always
   reflects what the caller passes in.

   Subreddits are intentionally NOT shown here. They live in the Subreddit
   radar on the profile page, which is the single surface for adding /
   removing communities. */

const EMPTY_PLAN = {
  target_customers: [],
  jobs_to_be_done: [],
  pain_points: [],
  trigger_phrases: [],
  suggested_subreddits: [],
  negative_matches: [],
  search_themes: [],
};

function planToMap(plan) {
  if (!plan) return null;
  return {
    customers: plan.target_customers || [],
    jobs: plan.jobs_to_be_done || [],
    pains: plan.pain_points || [],
    triggers: plan.trigger_phrases || [],
    negative: plan.negative_matches || [],
    themes: plan.search_themes || [],
  };
}

function OpportunityMapScreen({
  onApprove,
  onBack,
  mode = "onboard",
  searchPlan,
  editable = false,
  onChange,
  onSave,
  onCancelEdit,
  saving = false,
}) {
  const plan = searchPlan || EMPTY_PLAN;
  const map = planToMap(plan);

  const setListField = (key) => (next) => {
    onChange?.({ ...plan, [key]: next });
  };

  const handleSave = () => onSave?.(plan);

  return (
    <div className="screen-enter">
      <div className="onboard-panel" style={{ maxWidth: 860 }}>
        {mode === "onboard" && (
          <div className="onboard-header">
            <div className="onboard-eyebrow">Step 2 of 2</div>
            <h1 className="onboard-title">Here's what we'll look for.</h1>
            <p className="onboard-subtitle">
              We expanded your profile into a search plan. Review it, edit anything that feels off, and approve to
              start your first scan.
            </p>
          </div>
        )}

        <div className="map-grid">
          <MapCard
            title="Target customers"
            icon="User"
            items={map.customers}
            editable={editable}
            onChange={setListField("target_customers")}
          />
          <MapCard
            title="Jobs to be done"
            icon="Target"
            items={map.jobs}
            editable={editable}
            onChange={setListField("jobs_to_be_done")}
          />
          <MapCard
            title="Pain points we'll match"
            icon="Alert"
            items={map.pains}
            editable={editable}
            onChange={setListField("pain_points")}
          />
          <MapCard
            title="Trigger phrases"
            icon="Sparkles"
            chips={map.triggers}
            mono
            editable={editable}
            onChange={setListField("trigger_phrases")}
          />
          <MapCard
            title="Negative matches"
            icon="X"
            items={map.negative}
            soft
            editable={editable}
            onChange={setListField("negative_matches")}
          />
          <MapCard
            title="Search themes"
            icon="Tag"
            chips={map.themes}
            editable={editable}
            onChange={setListField("search_themes")}
          />
        </div>

        {editable && (
          <div className="onboard-actions">
            <span className="small-note">
              <window.I.Edit size={12} style={{ verticalAlign: "-2px" }} />
              {" "}Editing search plan. Saves to your profile.
            </span>
            <div className="spacer" />
            <button className="btn btn-secondary" onClick={onCancelEdit} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <window.I.Refresh size={14} style={{ animation: "spin 900ms linear infinite" }} />
                  Saving…
                </>
              ) : (
                <>
                  <window.I.Check size={14} />
                  Save changes
                </>
              )}
            </button>
          </div>
        )}

        {mode === "onboard" && !editable && (
          <div className="onboard-actions">
            <span className="small-note">
              You can re-run this analysis any time from your profile.
            </span>
            <div className="spacer" />
            <button className="btn btn-secondary" onClick={onBack}>
              <window.I.Edit size={14} /> Edit profile
            </button>
            <button className="btn btn-primary" onClick={onApprove}>
              <window.I.Sparkles size={14} />
              Start first scan
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function MapCard({ title, icon, items, chips, mono = false, soft = false, editable = false, onChange }) {
  const Cmp = window.I[icon];
  const values = items || chips || [];
  return (
    <div className="card">
      <div className="card-header">
        <Cmp size={14} style={{ color: soft ? "var(--danger-text)" : "var(--text-tertiary)" }} />
        <span className="card-title">{title}</span>
      </div>
      <div className="card-body" style={{ padding: "12px 18px 16px" }}>
        {editable ? (
          <EditableList
            values={values}
            onChange={onChange}
            mono={mono}
          />
        ) : items ? (
          <ul style={{
            listStyle: "none", padding: 0, margin: 0,
            display: "flex", flexDirection: "column", gap: 8,
          }}>
            {items.map((it, i) => (
              <li key={i} style={{
                fontSize: 12.5, color: "var(--text-secondary)",
                lineHeight: 1.5, display: "flex", gap: 8,
              }}>
                <span style={{
                  width: 4, height: 4, borderRadius: "50%",
                  background: soft ? "var(--danger)" : "var(--accent)",
                  marginTop: 8, flexShrink: 0,
                }} />
                {it}
              </li>
            ))}
          </ul>
        ) : (
          <div className="chip-list" style={{ marginTop: 2 }}>
            {chips.map((c) => (
              <span key={c} className="pill pill-neutral" style={mono ? { fontFamily: "var(--font-mono)" } : null}>
                {c}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EditableList({ values, onChange, mono = false }) {
  const update = (i, next) =>
    onChange(values.map((v, j) => (j === i ? next : v)));
  const remove = (i) => onChange(values.filter((_, j) => j !== i));
  const add = () => onChange([...values, ""]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {values.map((v, i) => (
        <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            className="input"
            value={v}
            onChange={(e) => update(i, e.target.value)}
            style={mono ? { fontFamily: "var(--font-mono)", fontSize: 12.5 } : { fontSize: 12.5 }}
          />
          <button
            className="btn-ghost"
            onClick={() => remove(i)}
            style={{ padding: 6, borderRadius: 6, color: "var(--text-tertiary)" }}
            title="Remove"
          >
            <window.I.X size={14} />
          </button>
        </div>
      ))}
      <button className="btn btn-ghost btn-sm" onClick={add} style={{ alignSelf: "flex-start" }}>
        <window.I.Plus size={12} /> Add
      </button>
    </div>
  );
}

window.OpportunityMapScreen = OpportunityMapScreen;
